import React from "react";
import ChariotConnect from "react-chariot-connect";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { unwrapResult } from "@reduxjs/toolkit";

import {
  setCampaignThankOpen,
  setCommunityThankOpen,
  setDonationFormOpen,
  setStripeOpen,
} from "../../redux/state/donationSlice";
import {
  donateCampaignChariotRequest,
  getCommunityDonations,
  grandDonation,
} from "../../redux/state/paymentSlice";
import { getCommunity } from "../../redux/state/communitySlice";

const ID = process.env.REACT_APP_CHARIOT_KEY;

const Chariot = () => {
  const dispatch = useDispatch();
  const { communityId } = useParams();

  const isAdmin = useSelector((state) => state.community.community.role, shallowEqual);
  const body = useSelector((state) => state.donation.body, shallowEqual);

  // Log component initialization
  console.log('[CHARIOT] Component initialized:', {
    timestamp: new Date().toISOString(),
    communityId: communityId,
    isAdmin: isAdmin,
    donationBody: body,
    chariotKey: ID ? 'Present' : 'Missing',
    hasAmount: !!body.amount,
    amount: body.amount
  });

  const onChariotSuccess = (e) => {
    console.log('[CHARIOT] Success event received:', {
      timestamp: new Date().toISOString(),
      eventType: e.type,
      eventDetail: e.detail,
      fullEvent: e
    });

    if (e.type !== "CHARIOT_SUCCESS" || !e.detail) {
      console.error('[CHARIOT] Invalid success event:', {
        expectedType: 'CHARIOT_SUCCESS',
        actualType: e.type,
        hasDetail: !!e.detail
      });
      toast.info("Something went wrong");
      return;
    }

    const chariotWorkflowSessionId = e.detail.workflowSessionId;
    const amount = !!e.detail.grantIntent?.amount ? e.detail.grantIntent.amount / 100 : 0;
    const paymentId = e.detail.grantIntent?.metadata?.paymentId;

    console.log('[CHARIOT] Processing success event data:', {
      workflowSessionId: chariotWorkflowSessionId,
      amount: amount,
      paymentId: paymentId,
      grantIntent: e.detail.grantIntent,
      metadata: e.detail.grantIntent?.metadata
    });

    console.log('[CHARIOT] Calling grandDonation API with payload:', {
      paymentId,
      chariotWorkflowSessionId,
      amount
    });

    dispatch(
      grandDonation({
        paymentId,
        chariotWorkflowSessionId,
        amount,
      })
    )
      .then(unwrapResult)
      .then((response) => {
        console.log('[CHARIOT] grandDonation API success response:', {
          timestamp: new Date().toISOString(),
          response: response,
          paymentId: paymentId,
          workflowSessionId: chariotWorkflowSessionId,
          amount: amount
        });
        toast.info("Success");
        chariotPaymentSuccessHandler();
      })
      .catch((error) => {
        console.error('[CHARIOT] grandDonation API error:', {
          timestamp: new Date().toISOString(),
          error: error,
          errorTitle: error.title,
          errorMessage: error.message,
          paymentId: paymentId,
          workflowSessionId: chariotWorkflowSessionId,
          amount: amount
        });
        toast.info(error.title || 'An error occurred');
      });
  };

  const chariotPaymentSuccessHandler = () => {
    console.log('[CHARIOT] Payment success handler called:', {
      timestamp: new Date().toISOString(),
      communityId: communityId,
      campaignId: body.campaignId,
      isAdmin: isAdmin
    });

    dispatch(setStripeOpen(false));
    if (!communityId) {
      console.warn('[CHARIOT] No communityId available for success handler');
      return;
    }
    
    const campaignId = body.campaignId;
    if (!campaignId) {
      console.log('[CHARIOT] Community donation success - refreshing community data:', {
        communityId: communityId,
        isAdmin: isAdmin
      });
      
      dispatch(getCommunity(communityId));
      dispatch(
        getCommunityDonations({
          isAdmin,
          communityId,
          page: 1,
          pageSize: 8,
        })
      );
      dispatch(setCommunityThankOpen(true));
      return;
    }
    
    console.log('[CHARIOT] Campaign donation success - opening campaign thank you:', {
      campaignId: campaignId,
      communityId: communityId
    });
    dispatch(setCampaignThankOpen(true));
  };

  const onDonationRequest = () => {
    const campaignId = body.incomingBalanceId;
    
    console.log('[CHARIOT] Donation request initiated:', {
      timestamp: new Date().toISOString(),
      campaignId: campaignId,
      communityId: communityId,
      donationBody: body,
      hasAmount: !!body.amount,
      amount: body.amount
    });

    if (!campaignId || !communityId) {
      console.error('[CHARIOT] Missing required parameters for donation request:', {
        hasCampaignId: !!campaignId,
        hasCommunityId: !!communityId,
        campaignId: campaignId,
        communityId: communityId
      });
      return;
    }

    dispatch(setDonationFormOpen(false));
    
    console.log('[CHARIOT] Calling donateCampaignChariotRequest API with payload:', {
      campaignId,
      communityId,
      body
    });

    return dispatch(
      donateCampaignChariotRequest({
        campaignId,
        communityId,
        body,
      })
    )
      .then(unwrapResult)
      .then((response) => {
        console.log('[CHARIOT] donateCampaignChariotRequest API success response:', {
          timestamp: new Date().toISOString(),
          response: response,
          campaignId: campaignId,
          communityId: communityId,
          donationAmount: body.amount
        });
        return response;
      })
      .catch((error) => {
        console.error('[CHARIOT] donateCampaignChariotRequest API error:', {
          timestamp: new Date().toISOString(),
          error: error,
          errorTitle: error.title,
          errorMessage: error.message,
          campaignId: campaignId,
          communityId: communityId,
          donationAmount: body.amount
        });
        toast.info(error.title || 'An error occurred');
      });
  };

  return (
    <div style={{ marginBottom: "16px", borderRadius: "10px", overflow: "hidden" }}>
      <ChariotConnect
        cid={ID}
        onDonationRequest={onDonationRequest}
        onSuccess={onChariotSuccess}
        onError={(error) => {
          console.error('[CHARIOT] Widget error event:', {
            timestamp: new Date().toISOString(),
            error: error,
            errorMessage: error.message,
            errorCode: error.code
          });
        }}
        onReady={() => {
          console.log('[CHARIOT] Widget ready:', {
            timestamp: new Date().toISOString(),
            chariotKey: ID ? 'Present' : 'Missing'
          });
        }}
        theme={{
          height: "h-10",
        }}
        disabled={!body.amount}
      />
    </div>
  );
};

export default Chariot;
